import React, { useCallback, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  getAdminVerificationYearlyDataYear,
  useCacheInvalidation,
  useGetCarbonProgramByFarmId,
  useGetFarmPayoutApprovalsAdmin,
  useGetFarmVerificationSummary,
  useGetFieldVerificationSummary,
  useParams,
} from "utils";

import {
  ADMIN_FIELD_VERIFICATIONS,
  FARM_VERIFICATION_SUMMARY,
  FIELD_VERIFICATION_SUMMARY,
  GET_FARM_COVER_CROP_DETECTIONS,
  GET_FARM_DOC_SCAN_FIELDS,
  GET_FARM_DOC_SCAN_SUMMARY,
  GET_FIELD_PRACTICES,
  GET_FIELD_VERIFICATIONS,
} from "consts";

import { MainPage, TabLabel, Tabs } from "components";

import FootprintingVerificationsAssessmentList from "pages/admin/verifications/components/footprinting/FootprintVerificationsAssessmentList";
import { CropList } from "pages/admin/verifications/components/lists/CropList";
import { FertilizerList } from "pages/admin/verifications/components/lists/FertilizerList";
import { FieldList } from "pages/admin/verifications/components/lists/FieldList";
import { TillageAndSeedingList } from "pages/admin/verifications/components/lists/TillageAndSeedingList";
import { Documents } from "pages/admin/verifications/components/misc/Documents";
import { FarmDashboard } from "pages/admin/verifications/components/misc/FarmDashboard";
import { FarmHeader } from "pages/admin/verifications/components/misc/FarmHeader";
import { FarmOverview } from "pages/admin/verifications/components/misc/FarmOverview";
import { PayoutHistory } from "pages/admin/verifications/components/misc/PayoutHistory";

const REFETCH_INTERVAL = 15000; // 15 seconds

export const Farm = () => {
  const { farmId, section } = useParams();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState(section);

  // React Query hooks for data fetching
  const {
    data: farmVerificationData,
    isLoading: isLoadingFarmSummary,
    refetch: refetchFarmSummary,
  } = useGetFarmVerificationSummary({
    farmId,
    refetchInterval: REFETCH_INTERVAL,
  });

  const {
    data: fieldVerificationSummary,
    isLoading: isLoadingFieldSummary,
    refetch: refetchFieldSummary,
  } = useGetFieldVerificationSummary({
    farmId,
    refetchInterval: REFETCH_INTERVAL,
  });

  const { data: payoutApprovals, isLoading: isLoadingPayouts, refetch: refetchPayoutApprovals } = useGetFarmPayoutApprovalsAdmin({ farmId });

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);

  // Extract farm and summary from the combined response
  const farm = farmVerificationData?.farm;
  const farmVerificationSummary = farmVerificationData?.summary;

  useCacheInvalidation(true, farm?.userId);

  // Create a comprehensive refresh function that invalidates all verification queries
  const refreshAllVerificationData = useCallback(() => {
    // Invalidate all verification-related queries
    queryClient.invalidateQueries({ queryKey: [FARM_VERIFICATION_SUMMARY, farmId] });
    queryClient.invalidateQueries({ queryKey: [FIELD_VERIFICATION_SUMMARY, farmId] });
    queryClient.invalidateQueries({ queryKey: [ADMIN_FIELD_VERIFICATIONS, farmId] });
    queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICES] });
    queryClient.invalidateQueries({ queryKey: [GET_FIELD_VERIFICATIONS, farmId] });
    queryClient.invalidateQueries({ queryKey: [GET_FARM_COVER_CROP_DETECTIONS, farmId] });
    queryClient.invalidateQueries({ queryKey: [GET_FARM_DOC_SCAN_SUMMARY, farmId] });
    queryClient.invalidateQueries({ queryKey: [GET_FARM_DOC_SCAN_FIELDS, farmId] });
  }, [farmId, queryClient]);

  const handleFarmUpdate = useCallback(() => {
    refetchFarmSummary();
  }, [refetchFarmSummary]);

  const handleFieldUpdate = useCallback(() => {
    refetchFieldSummary();
    refetchFarmSummary();
    queryClient.invalidateQueries({ queryKey: [GET_FARM_DOC_SCAN_SUMMARY, farmId] });
    queryClient.invalidateQueries({ queryKey: [GET_FARM_DOC_SCAN_FIELDS, farmId] });
  }, [refetchFieldSummary, refetchFarmSummary, farmId, queryClient]);

  const handleDashboardUpdate = useCallback(() => {
    refetchFarmSummary();
    refetchPayoutApprovals();
  }, [refetchFarmSummary, refetchPayoutApprovals]);

  const verificationYear = getAdminVerificationYearlyDataYear();
  const hasCurrentYearVerificationApproval = !!payoutApprovals?.filter(({ year }) => year === verificationYear).length;
  const hasAnyApproval = !!payoutApprovals?.length;

  const isLoading = isLoadingPayouts || isLoadingFarmSummary || isLoadingFieldSummary;

  return (
    <MainPage pageName="admin" loading={isLoading}>
      {(section === "dashboard" ||
        section === "documents" ||
        section === "farm" ||
        section === "fields" ||
        section === "crops" ||
        section === "fertilizers" ||
        section === "tillage-and-seeding" ||
        section === "footprinting" ||
        carbonProgram?.fieldsManagement?.practices?.length) && (
        <>
          <FarmHeader
            farm={farm}
            userId={farm?.userId}
            onUpdate={handleFarmUpdate}
            taskStatus={farmVerificationSummary?.verificationTaskStatus}
            loadingFarm={isLoadingFarmSummary || !farm}
            verificationSummary={farmVerificationSummary}
          />
          <Tabs
            onChange={(e) => setActiveTab(e)}
            activeKey={activeTab}
            defaultActiveKey={section || activeTab || "dashboard"}
            items={[
              {
                label: (
                  <TabLabel dotColor="red" showDot={farm?.verified === false}>
                    dashboard
                  </TabLabel>
                ),
                key: "dashboard",
                children: (
                  <FarmDashboard
                    farmId={farmId}
                    farmIsVerified={farm?.verified}
                    verificationSummary={farmVerificationSummary}
                    fieldVerificationSummary={fieldVerificationSummary}
                    carbonProgram={carbonProgram}
                    setActiveTab={setActiveTab}
                    onUpdate={handleDashboardUpdate}
                    payoutApproved={hasCurrentYearVerificationApproval}
                  />
                ),
              },
              {
                label: <TabLabel>documents</TabLabel>,
                key: "documents",
                children: <Documents farmId={farmId} farmCountry={farm?.country} farmName={farm?.name} />,
              },
              {
                label: (
                  <TabLabel dotColor="yellow" showDot={farm?.verified === null}>
                    farm
                  </TabLabel>
                ),
                key: "farm",
                children: <FarmOverview farm={farm} farmId={farmId} onUpdate={handleFarmUpdate} points={farmVerificationSummary?.points} />,
              },
              {
                label: (
                  <TabLabel dotColor="yellow" showDot={fieldVerificationSummary?.fieldPendingVerificationCount > 0}>
                    fields
                  </TabLabel>
                ),
                key: "fields",
                children: (
                  <FieldList
                    farm={farm}
                    farmId={farmId}
                    onUpdate={handleFieldUpdate}
                    summary={fieldVerificationSummary}
                    payoutApproved={hasCurrentYearVerificationApproval}
                  />
                ),
              },
              {
                label: (
                  <TabLabel dotColor="yellow" showDot={(farmVerificationSummary?.practiceStatusBySlug?.crops?.pending ?? 0) > 0}>
                    crops
                  </TabLabel>
                ),
                key: "crops",
                children: <CropList farmId={farmId} onUpdate={refreshAllVerificationData} />,
              },
              {
                label: (
                  <TabLabel
                    dotColor="yellow"
                    showDot={
                      (farmVerificationSummary?.practiceStatusBySlug?.mineralFertilizers?.pending ?? 0) +
                        (farmVerificationSummary?.practiceStatusBySlug?.organicFertilizers?.pending ?? 0) >
                      0
                    }
                  >
                    fertilizers
                  </TabLabel>
                ),
                key: "fertilizers",
                children: <FertilizerList farmId={farmId} onUpdate={refreshAllVerificationData} />,
              },
              {
                label: (
                  <TabLabel
                    dotColor="yellow"
                    showDot={
                      (farmVerificationSummary?.practiceStatusBySlug?.tillages?.pending ?? 0) +
                        (farmVerificationSummary?.practiceStatusBySlug?.seedings?.pending ?? 0) >
                      0
                    }
                  >
                    tillage & seeding
                  </TabLabel>
                ),
                key: "tillage-and-seeding",
                children: <TillageAndSeedingList farmId={farmId} onUpdate={refreshAllVerificationData} />,
              },
              carbonProgram?.platformFeatures?.footprinting?.enabled
                ? {
                    label: <TabLabel>footprinting</TabLabel>,
                    key: "footprinting",
                    children: <FootprintingVerificationsAssessmentList farmId={farmId} />,
                  }
                : null,
              hasAnyApproval
                ? {
                    label: <TabLabel>payout history</TabLabel>,
                    key: "payout",
                    children: <PayoutHistory payouts={payoutApprovals} currency={carbonProgram?.currency} />,
                  }
                : null,
            ]}
          />
        </>
      )}
    </MainPage>
  );
};
